import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { showToast } from '@/utils/toast';

const PROOF_MAX_FILES = 6;
const PROOF_ACCEPT = 'image/*,.pdf,application/pdf';

function isPdfUrl(url) {
  const s = String(url || '').toLowerCase();
  return s.includes('.pdf') || s.includes('application/pdf');
}

/**
 * 建议零售价证明材料上传（图片缩略图 / PDF 卡片 + 拖拽 + 证明链接）。
 * 编辑弹窗与新建建议模板共用。
 */
export default function RrpProofUploadBlock({
  title,
  shopId,
  busy = false,
  attachments,
  proofLink,
  onChange,
  linkPlaceholder,
}) {
  const { t } = useTranslation('workbench');
  const linkText = linkPlaceholder || t('rrpOps.proofLinkPh');
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const files = Array.isArray(attachments) ? attachments : [];
  const canUploadMore = files.length < PROOF_MAX_FILES;
  const disabled = busy || uploading || !shopId || !canUploadMore;

  const uploadFiles = async (fileList) => {
    if (!shopId || disabled) return;
    const picked = Array.from(fileList || []).filter(Boolean);
    if (!picked.length) return;
    const remain = Math.max(0, PROOF_MAX_FILES - files.length);
    if (remain <= 0) {
      showToast(t('rrpOps.proofMax', { max: PROOF_MAX_FILES }), 'warning');
      return;
    }
    const batch = picked.slice(0, remain);
    if (picked.length > remain) {
      showToast(t('rrpOps.proofRemain', { count: remain }), 'warning');
    }
    setUploading(true);
    try {
      const urls = [];
      for (const file of batch) {
        const res = await temuPlatformAPI.uploadDiscussFile(shopId, file, 4);
        const url = res?.url;
        if (!url) throw new Error(t('rrpOps.uploadNoUrl'));
        urls.push(url);
      }
      onChange({
        attachments: [...files, ...urls].slice(0, PROOF_MAX_FILES),
        proofLink,
      });
      showToast(urls.length > 1 ? t('rrpOps.uploadedCount', { count: urls.length }) : t('rrpOps.proofUploaded'), 'success');
    } catch (e) {
      showToast(e?.message || t('photoLib.uploadFailed'), 'error');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = (url) => {
    onChange({
      attachments: files.filter((u) => u !== url),
      proofLink,
    });
  };

  return (
    <div className="tpo-rrp-edit-proof tpo-rrp-tpl-proof tpo-img-upload">
      <div className="tpo-img-upload__head tpo-rrp-tpl-proof__head">
        <div className="tpo-rrp-edit-proof__title">
          {title}
          <span className="tpo-discuss-requote__optional">{t('rrpOps.optionalParen')}</span>
        </div>
        <span className="tpo-rrp-tpl-proof__count">{t('rrpOps.fileCount', { current: files.length, max: PROOF_MAX_FILES })}</span>
      </div>
      <div className="tpo-img-upload__grid tpo-rrp-tpl-proof__grid">
        {canUploadMore ? (
          <button
            type="button"
            className={`tpo-img-upload__drop${dragOver ? ' tpo-img-upload__drop--over' : ''}${uploading ? ' tpo-img-upload__drop--busy' : ''}`}
            disabled={busy || uploading || !shopId}
            aria-busy={uploading}
            onClick={() => inputRef.current?.click()}
            onDragEnter={(e) => {
              e.preventDefault();
              if (!busy && !uploading && shopId) setDragOver(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (!busy && !uploading && shopId) setDragOver(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragOver(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (busy || uploading || !shopId) return;
              uploadFiles(e.dataTransfer?.files);
            }}
          >
            {uploading ? (
              <span className="tpo-img-upload__busy" aria-live="polite">
                <span className="tpo-img-upload__spinner" aria-hidden="true" />
                <span className="tpo-img-upload__busy-text">{t('photoLib.uploading')}</span>
              </span>
            ) : (
              <>
                <span className="tpo-img-upload__plus" aria-hidden="true">+</span>
                <span>{t('rrpOps.clickOrDrop')}</span>
              </>
            )}
          </button>
        ) : null}
        {files.map((url) => {
          const pdf = isPdfUrl(url);
          return (
            <div key={url} className="tpo-img-upload__thumb tpo-rrp-tpl-proof__thumb">
              {pdf ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="tpo-rrp-tpl-proof__pdf"
                  title={url}
                >
                  PDF
                </a>
              ) : (
                <a href={url} target="_blank" rel="noreferrer" title={url}>
                  <img src={url} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" />
                </a>
              )}
              {busy || uploading ? null : (
                <button
                  type="button"
                  className="tpo-img-upload__remove"
                  aria-label={t('rrpOps.removeFile')}
                  onClick={() => remove(url)}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={PROOF_ACCEPT}
        multiple
        hidden
        disabled={busy || uploading || !shopId || !canUploadMore}
        onChange={(e) => {
          const list = e.target.files;
          e.target.value = '';
          if (list?.length) uploadFiles(list);
        }}
      />
      <input
        className="tpo-input"
        disabled={busy}
        value={proofLink || ''}
        onChange={(e) => onChange({ attachments: files, proofLink: e.target.value })}
        placeholder={linkText}
        maxLength={1000}
      />
    </div>
  );
}
